import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { OrganizationProfileSettingsDto, ResponseDto } from "@mimber/schema"
import { organizationKeys } from "../organization.utils"

interface UploadLogoVariables {
  file: File
}

interface UploadOrganizationLogoOptions extends Omit<
  UseMutationOptions<
    ResponseDto<OrganizationProfileSettingsDto>,
    Error,
    UploadLogoVariables
  >,
  "mutationFn"
> {}

/**
 * Hook for uploading organization logo
 *
 * Uploads a logo file to S3 and updates the organization's logo storage item.
 * Automatically generates thumbnail via Lambda function.
 *
 * @param uuid - Organization UUID
 * @param options - Additional options for the mutation
 * @returns Mutation hook for uploading logo
 *
 * @example
 * ```tsx
 * const uploadLogo = useUploadOrganizationLogo(organizationUuid, {
 *   onSuccess: () => {
 *     toast.success('Logo uploaded successfully!')
 *   }
 * })
 *
 * // In file input onChange handler
 * const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0]
 *   if (file) {
 *     uploadLogo.mutate({ file })
 *   }
 * }
 * ```
 */
export const useUploadOrganizationLogo = (
  uuid: string,
  options: UploadOrganizationLogoOptions = {}
): UseMutationResult<
  ResponseDto<OrganizationProfileSettingsDto>,
  Error,
  UploadLogoVariables
> => {
  guardAppUsage("mimber-organization", "useUploadOrganizationLogo")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ file }: UploadLogoVariables) => {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append("logo", file)

      // Upload logo
      const response = await server.post<
        ResponseDto<OrganizationProfileSettingsDto>
      >(`organizations/${uuid}/logo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate current organization query to refetch with new logo storage item
      queryClient.invalidateQueries({
        queryKey: organizationKeys.getCurrentOrganization(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
